import { isVariableFolder } from 'services/entities/VariablesEntity';
import type { VariableShape } from 'services/entities/VariablesEntity';

export type CrmArrayLeaf = {
  name: string;
  friendlyName?: string;
  type?: string;
  value?: unknown;
  objectId?: string;
  template?: string;
  path?: string;
  shape?: VariableShape;
  variables?: CrmArrayLeaf[];
};

export type CrmPasteField = {
  template: string;
  instances: CrmArrayLeaf[];
  chipLeaf: CrmArrayLeaf;
};

const PVAR_MARKER_REGEX = /\[\[pvar:([\s\S]+)\]\]/;

export function parseArrayPayload(clipboardText: string): CrmArrayLeaf | null {
  const match = PVAR_MARKER_REGEX.exec(clipboardText);
  if (!match) return null;
  try {
    const payload = JSON.parse(match[1].trim());
    const data = payload?.data;
    if (data?.shape?.type !== 'array' || !Array.isArray(data.variables) || data.variables.length === 0) return null;
    return data as CrmArrayLeaf;
  } catch {
    return null;
  }
}

export function collectDeepLeaves(node: CrmArrayLeaf): CrmArrayLeaf[] {
  return (node.variables ?? []).flatMap((child) =>
    child?.variables?.length ? collectDeepLeaves(child) : child?.name && !isVariableFolder(child) ? [child] : []
  );
}

export function buildPasteFields(
  payload: CrmArrayLeaf,
  opts: { grouped: boolean; isKnownField: (name: string) => boolean }
): CrmPasteField[] {
  const { grouped, isKnownField } = opts;

  if (grouped) {
    // Grouped arrays carry instances in node.variables — never deep-flatten;
    // the backend-emitted structure is canonical. Only direct leaf fields of
    // each instance participate, keyed by field name.
    const instances = payload.variables ?? [];

    // Derived field entry (panel field schema): instances ARE the field's leaves —
    // no `.item` children. The payload is a single field, one row per leaf instance.
    const isLeafInstances = instances.every((instance) => !instance.variables?.length);
    if (isLeafInstances) {
      const template = instances[0]?.template ?? payload.name;
      const chipLeaf: CrmArrayLeaf = {
        name: template,
        friendlyName: payload.friendlyName ?? template,
        template,
        path: payload.path,
      };
      return [{ template, instances, chipLeaf }];
    }

    const fieldsByName = new Map<string, CrmArrayLeaf[]>();

    instances.forEach((instance) => {
      (instance.variables ?? []).forEach((field) => {
        if (!field.name) return;
        const key = field.name;
        const list = fieldsByName.get(key) ?? [];
        list.push(field);
        fieldsByName.set(key, list);
      });
    });

    return [...fieldsByName.entries()]
      .map(([name, leaves]): CrmPasteField | null => {
        if (leaves.length === 0) return null;
        const chipLeaf: CrmArrayLeaf = isKnownField(name)
          ? { name, friendlyName: leaves[0]?.friendlyName ?? name, template: name }
          : leaves[0];
        return { template: name, instances: leaves, chipLeaf };
      })
      .filter((field): field is CrmPasteField => field !== null);
  }

  // Non-grouped: flatten and collect all leaves
  const allLeaves = collectDeepLeaves(payload);
  if (allLeaves.length === 0) return [];
  return [{ template: allLeaves[0].name, instances: allLeaves, chipLeaf: allLeaves[0] }];
}
